import { Engine } from '@babylonjs/core/Engines/engine';
import { Scene } from '@babylonjs/core/scene';
import { Color3, Color4, Vector3 } from '@babylonjs/core/Maths/math';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import { ShadowGenerator } from '@babylonjs/core/Lights/Shadows/shadowGenerator';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import '@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent';
import '@babylonjs/core/Rendering/depthRendererSceneComponent';
import { installWorldShadows } from './sunshadow.js';
import { installWorldAO } from './ao.js';
import { installAtmosphere } from './atmosphere.js';

// Default: flat test arena (gym). opts.map = MAP10K terrain world instead —
// no checker ground/crates, long fog so the 10 km landscape reads.
export function createWorld(canvas, opts = {}) {
  const engine = new Engine(canvas, true, { stencil: true });
  const scene = new Scene(engine);
  scene.clearColor = new Color4(0.62, 0.72, 0.82, 1); // overcast sky
  // ROUND-009. THE MAP WORLD HAS NO LINEAR FOG ANY MORE.
  // Linear fog is a dead band followed by a lerp: on the clear mood nothing
  // inside 600-700 m received a single point of haze and everything past it was
  // a flat grey ramp of one colour, which is why our frames read as flat next to
  // a DayZ plate (see the header of src/core/atmosphere.js). The world's own
  // self-lit ShaderMaterials now run a two-term exponential atmosphere with a
  // height-integrated ground layer and a view-dependent inscatter colour; this
  // is the Babylon-side half of the same model, and it exists so the handful of
  // Standard/PBR meshes in the scene (the character, the kenney props, remote
  // players) extinguish on the SAME curve as the terrain they stand on rather
  // than on a second, differently-shaped one.
  // FOGMODE_EXP2 is exp(-(d*density)^2) — bit-for-bit the Rayleigh term in
  // atmosphere.js. SkySystem.applyEnv drives fogDensity per mood; the value here
  // only covers the frames before the first mood lands. fogStart/fogEnd stay
  // authored because five world modules seed their own fogSE uniform from them
  // at material-construction time (and the gym below is still linear).
  scene.fogMode = opts.map ? Scene.FOGMODE_EXP2 : Scene.FOGMODE_LINEAR;
  scene.fogDensity = 0.00037;
  scene.fogStart = opts.map ? 600 : 60;
  scene.fogEnd = opts.map ? 4200 : 220;
  scene.fogColor = new Color3(0.62, 0.72, 0.82);

  const hemi = new HemisphericLight('hemi', new Vector3(0, 1, 0), scene);
  hemi.intensity = 0.75;
  hemi.groundColor = new Color3(0.35, 0.33, 0.3);

  const sun = new DirectionalLight('sun', new Vector3(-0.4, -0.85, 0.35), scene);
  sun.position = new Vector3(30, 60, -30);
  sun.intensity = 1.1;

  // MAP10K: real cast shadows. The generator lives in sunshadow.js because the
  // world's self-lit ShaderMaterials have to sample the depth map themselves —
  // see the header there. It replaces the gym generator entirely (a light can
  // only own one), so main.js's addShadowCaster() calls for the character,
  // dummies and weapon props feed straight into it.
  if (opts.map) {
    // BEFORE the shadow install, deliberately: ambient occlusion
    // (src/core/ao.js) string-patches the same world fragment shaders, and its
    // `+ ambUp * ashAO)` has to be written before sunshadow's `+ ambUp *`
    // split consumes that anchor. Order is a contract here, not a preference.
    const ao = installWorldAO(scene);
    const sunShadows = installWorldShadows(scene, sun);
    // AFTER the shadow install, deliberately: the atmosphere patch unwraps
    // sunshadow's own receiver-height fog term (it integrates height along the
    // ray instead, so the two would stack). See src/core/atmosphere.js.
    const atmo = installAtmosphere(scene);
    return { engine, scene, shadows: sunShadows.gen, sunShadows, atmo, ao };
  }

  const shadows = new ShadowGenerator(2048, sun);
  shadows.useExponentialShadowMap = true;
  shadows.darkness = 0.35;


  // Checkerboard ground (1 m squares) so foot planting/sliding is obvious.
  const ground = MeshBuilder.CreateGround('ground', { width: 400, height: 400, subdivisions: 4 }, scene);
  const gmat = new StandardMaterial('gmat', scene);
  const gtex = new DynamicTexture('checker', { width: 1024, height: 1024 }, scene, true);
  gtex.wrapU = Texture.WRAP_ADDRESSMODE; // DynamicTexture defaults to clamp,
  gtex.wrapV = Texture.WRAP_ADDRESSMODE; // which silently breaks uScale tiling
  const ctx = gtex.getContext();
  const CELLS = 16, CS = 1024 / CELLS; // texture tiles every 16 m -> 1 m cells
  for (let y = 0; y < CELLS; y++) {
    for (let x = 0; x < CELLS; x++) {
      ctx.fillStyle = (x + y) % 2 ? '#454c40' : '#79816c';
      ctx.fillRect(x * CS, y * CS, CS, CS);
    }
  }
  gtex.update();
  gtex.anisotropicFilteringLevel = 8;
  gmat.diffuseTexture = gtex;
  gmat.diffuseTexture.uScale = 25;
  gmat.diffuseTexture.vScale = 25;
  gmat.specularColor = Color3.Black();
  ground.material = gmat;
  ground.receiveShadows = true;

  // Reference crates at varying distances (melee range checks, camera feel).
  const cmat = new StandardMaterial('cmat', scene);
  cmat.diffuseColor = new Color3(0.45, 0.35, 0.25);
  cmat.specularColor = Color3.Black();
  const spots = [[3, 0.5, 3], [-4, 0.5, 6], [8, 0.5, -2], [0, 0.5, 12], [-10, 0.5, -8], [15, 0.5, 10]];
  for (const [x, y, z] of spots) {
    const c = MeshBuilder.CreateBox('crate', { size: 1 }, scene);
    c.position.set(x, y, z);
    c.material = cmat;
    shadows.addShadowCaster(c);
    c.receiveShadows = true;
  }

  return { engine, scene, shadows };
}
